package io.github.retar.portfolio.pages

import androidx.compose.runtime.*
import com.varabyte.kobweb.core.Page
import com.varabyte.kobweb.core.data.*
import com.varabyte.kobweb.core.layout.Layout
import com.varabyte.kobweb.core.layout.NoLayout
import com.varabyte.kobweb.core.init.InitRoute
import com.varabyte.kobweb.core.init.InitRouteContext
import com.varabyte.kobwebx.frontmatter.*
import com.varabyte.kobwebx.markdown.*

@InitRoute
fun initInfinite_carousel_kobwebPage(ctx: InitRouteContext) {
    ctx.data.add(MarkdownContext("infinite_carousel_kobweb.md", FrontMatterElement.Builder { addScalar("title", "Building a smooth, infinite carousel in Kobweb"); addScalar("description", "A gapless, endlessly looping carousel in Kobweb and Silk: one CSS transform, no timers, no scroll code."); addScalar("date", "2025-11-17"); addScalar("cover", "/articles/infinite_carousel.jpeg"); }))
}

@Page("/blog/infinite-carousel")
@Layout(".pages.MarkdownPage")
@Composable
fun Infinite_carousel_kobwebPage() {
    org.jetbrains.compose.web.dom.H1(attrs = { id("building-a-smooth-infinite-carousel-in-kobweb") }) {
        org.jetbrains.compose.web.dom.Text("Building a smooth, infinite carousel in Kobweb")
    }
    org.jetbrains.compose.web.dom.P {
        org.jetbrains.compose.web.dom.Text("An earlier version of this site had a row of project cards that scrolled forever. My first attempt did it the obvious way: a coroutine that nudged the scroll position every frame and jumped back to the start at the end. It had every problem you'd expect. It stuttered in background tabs, because browsers throttle timers there. It jumped visibly when it reset. And it didn't scroll at all when there were only two or three cards.")
    }
    org.jetbrains.compose.web.dom.P {
        org.jetbrains.compose.web.dom.Text("The version that worked has no Kotlin running per frame. It's one CSS transform animation, a duplicated list and about forty lines of Silk.")
    }
    com.varabyte.kobweb.silk.components.layout.HorizontalDivider()
    org.jetbrains.compose.web.dom.H2(attrs = { id("the-trick-two-copies-and-a-50-slide") }) {
        org.jetbrains.compose.web.dom.Text("The trick: two copies and a 50% slide")
    }
    org.jetbrains.compose.web.dom.P {
        org.jetbrains.compose.web.dom.Text("Render the items twice in a single row, ")
        io.github.retar.portfolio.components.widgets.InlineCode("""[items][items]""")
        org.jetbrains.compose.web.dom.Text(", and animate the whole row from ")
        io.github.retar.portfolio.components.widgets.InlineCode("""translateX(0%)""")
        org.jetbrains.compose.web.dom.Text(" to ")
        io.github.retar.portfolio.components.widgets.InlineCode("""translateX(-50%)""")
        org.jetbrains.compose.web.dom.Text(".")
    }
    org.jetbrains.compose.web.dom.P {
        org.jetbrains.compose.web.dom.Text("When the animation reaches the end, the second copy sits exactly where the first one started, so jumping back to ")
        io.github.retar.portfolio.components.widgets.InlineCode("""0%""")
        org.jetbrains.compose.web.dom.Text(" is invisible. The loop is seamless by construction. There are no timers and no scroll APIs, and transforms are cheap for the browser to animate.")
    }
    org.jetbrains.compose.web.dom.P {
        org.jetbrains.compose.web.dom.Text("In Kobweb this takes three pieces:")
    }
    org.jetbrains.compose.web.dom.Ul {
        org.jetbrains.compose.web.dom.Li {
            org.jetbrains.compose.web.dom.Text("a ")
            org.jetbrains.compose.web.dom.B {
                org.jetbrains.compose.web.dom.Text("viewport")
            }
            org.jetbrains.compose.web.dom.Text(" that clips overflow,")
        }
        org.jetbrains.compose.web.dom.Li {
            org.jetbrains.compose.web.dom.Text("a ")
            org.jetbrains.compose.web.dom.B {
                org.jetbrains.compose.web.dom.Text("track")
            }
            org.jetbrains.compose.web.dom.Text(" that lays the items out in one row and runs the animation,")
        }
        org.jetbrains.compose.web.dom.Li {
            org.jetbrains.compose.web.dom.Text("a generic ")
            org.jetbrains.compose.web.dom.B {
                io.github.retar.portfolio.components.widgets.InlineCode("""InfiniteCarousel<T>""")
            }
            org.jetbrains.compose.web.dom.Text(" composable that takes any list.")
        }
    }
    com.varabyte.kobweb.silk.components.layout.HorizontalDivider()
    org.jetbrains.compose.web.dom.H2(attrs = { id("keyframes") }) {
        org.jetbrains.compose.web.dom.Text("Keyframes")
    }
    org.jetbrains.compose.web.dom.P {
        org.jetbrains.compose.web.dom.Text("The keyframes slide the track from ")
        io.github.retar.portfolio.components.widgets.InlineCode("""0%""")
        org.jetbrains.compose.web.dom.Text(" to ")
        io.github.retar.portfolio.components.widgets.InlineCode("""-50%""")
        org.jetbrains.compose.web.dom.Text(", half its own width:")
    }
    io.github.retar.portfolio.components.widgets.CodeBlock("""val InfiniteCarouselScrollKeyframes = Keyframes {
    0.percent {
        Modifier.transform { translateX(0.percent) }
    }
    100.percent {
        Modifier.transform { translateX((-50).percent) }
    }
}
""", lang = "kotlin")
    com.varabyte.kobweb.silk.components.layout.HorizontalDivider()
    org.jetbrains.compose.web.dom.H2(attrs = { id("the-track") }) {
        org.jetbrains.compose.web.dom.Text("The track")
    }
    org.jetbrains.compose.web.dom.P {
        org.jetbrains.compose.web.dom.Text("The track keeps everything in one row and runs the keyframes forever, at a constant speed:")
    }
    io.github.retar.portfolio.components.widgets.CodeBlock("""val InfiniteCarouselTrackStyle = CssStyle {
    base {
        Modifier
            .display(DisplayStyle.LegacyInlineFlex)
            .gap(24.px)
            .whiteSpace(WhiteSpace.NoWrap)
            .animation(
                InfiniteCarouselScrollKeyframes.toAnimation(
                    duration = 40.s,
                    iterationCount = AnimationIterationCount.Infinite,
                    timingFunction = AnimationTimingFunction.Linear,
                ),
            )
    }
    hover {
        Modifier.animation(
            InfiniteCarouselScrollKeyframes.toAnimation(
                duration = 40.s,
                iterationCount = AnimationIterationCount.Infinite,
                timingFunction = AnimationTimingFunction.Linear,
                playState = AnimationPlayState.Paused,
            ),
        )
    }
}
""", lang = "kotlin")
    org.jetbrains.compose.web.dom.P {
        org.jetbrains.compose.web.dom.Text("The ")
        io.github.retar.portfolio.components.widgets.InlineCode("""hover""")
        org.jetbrains.compose.web.dom.Text(" block pauses the carousel under the pointer, so people can actually read a card. It changes only ")
        io.github.retar.portfolio.components.widgets.InlineCode("""playState""")
        org.jetbrains.compose.web.dom.Text(" and keeps the same duration, because a different duration would make the track jump to a new position.")
    }
    org.jetbrains.compose.web.dom.P {
        io.github.retar.portfolio.components.widgets.InlineCode("""Linear""")
        org.jetbrains.compose.web.dom.Text(" matters too. With any easing curve, the track slows down and speeds up once per loop, and the seam becomes visible again.")
    }
    com.varabyte.kobweb.silk.components.layout.HorizontalDivider()
    org.jetbrains.compose.web.dom.H2(attrs = { id("the-viewport") }) {
        org.jetbrains.compose.web.dom.Text("The viewport")
    }
    org.jetbrains.compose.web.dom.P {
        org.jetbrains.compose.web.dom.Text("The viewport is a full-width flex container that hides whatever is outside it:")
    }
    io.github.retar.portfolio.components.widgets.CodeBlock("""val InfiniteCarouselViewportStyle = CssStyle {
    base {
        Modifier
            .fillMaxWidth()
            .display(DisplayStyle.Flex)
            .overflowX(Overflow.Hidden)
    }
}
""", lang = "kotlin")
    com.varabyte.kobweb.silk.components.layout.HorizontalDivider()
    org.jetbrains.compose.web.dom.H2(attrs = { id("the-composable") }) {
        org.jetbrains.compose.web.dom.Text("The composable")
    }
    io.github.retar.portfolio.components.widgets.CodeBlock("""@Composable
fun <T> InfiniteCarousel(
    items: List<T>,
    modifier: Modifier = Modifier,
    itemContent: @Composable (T) -> Unit,
) {
    // With only a few items, one copy is narrower than the screen and the loop shows a gap.
    val baseItems = if (items.size < 4) {
        generateSequence { items }.flatten().take(8).toList()
    } else {
        items
    }

    Box(modifier = InfiniteCarouselViewportStyle.toModifier().then(modifier)) {
        Box(modifier = InfiniteCarouselTrackStyle.toModifier()) {
            baseItems.forEach { itemContent(it) }
            // The second copy only exists to close the loop. Hide it from screen readers
            // and keyboard focus so the list is announced and tabbed through once.
            baseItems.forEach { item ->
                Box(Modifier.attr("aria-hidden", "true").attr("inert", "")) {
                    itemContent(item)
                }
            }
        }
    }
}
""", lang = "kotlin")
    org.jetbrains.compose.web.dom.P {
        org.jetbrains.compose.web.dom.Text("Two details make this robust.")
    }
    org.jetbrains.compose.web.dom.P {
        org.jetbrains.compose.web.dom.B {
            org.jetbrains.compose.web.dom.Text("Short lists.")
        }
        org.jetbrains.compose.web.dom.Text(" If one copy of the items is narrower than the viewport, the end of the second copy comes into view before the loop restarts, and you see empty space. Repeating short lists up to eight items fixes that for card-sized content. If your items are tiny, raise the number.")
    }
    org.jetbrains.compose.web.dom.P {
        org.jetbrains.compose.web.dom.B {
            org.jetbrains.compose.web.dom.Text("The duplicate is decoration.")
        }
        org.jetbrains.compose.web.dom.Text(" Without ")
        io.github.retar.portfolio.components.widgets.InlineCode("""aria-hidden""")
        org.jetbrains.compose.web.dom.Text(" and ")
        io.github.retar.portfolio.components.widgets.InlineCode("""inert""")
        org.jetbrains.compose.web.dom.Text(", a screen reader reads every card twice and the keyboard tabs through every link twice. Only the first copy is real content.")
    }
    com.varabyte.kobweb.silk.components.layout.HorizontalDivider()
    org.jetbrains.compose.web.dom.H2(attrs = { id("respect-reduced-motion") }) {
        org.jetbrains.compose.web.dom.Text("Respect reduced motion")
    }
    org.jetbrains.compose.web.dom.P {
        org.jetbrains.compose.web.dom.Text("A carousel that never stops moving is exactly what ")
        io.github.retar.portfolio.components.widgets.InlineCode("""prefers-reduced-motion""")
        org.jetbrains.compose.web.dom.Text(" exists for. Silk names the style's class after the property, so ")
        io.github.retar.portfolio.components.widgets.InlineCode("""InfiniteCarouselTrackStyle""")
        org.jetbrains.compose.web.dom.Text(" becomes ")
        io.github.retar.portfolio.components.widgets.InlineCode("""infinite-carousel-track""")
        org.jetbrains.compose.web.dom.Text(", and one rule in a stylesheet stops it:")
    }
    io.github.retar.portfolio.components.widgets.CodeBlock("""@media (prefers-reduced-motion: reduce) {
    .infinite-carousel-track { animation-play-state: paused !important; }
}
""", lang = "css")
    org.jetbrains.compose.web.dom.P {
        org.jetbrains.compose.web.dom.Text("People who asked their system for less motion get a static row of cards, and nothing else changes.")
    }
    com.varabyte.kobweb.silk.components.layout.HorizontalDivider()
    org.jetbrains.compose.web.dom.H2(attrs = { id("using-it") }) {
        org.jetbrains.compose.web.dom.Text("Using it")
    }
    org.jetbrains.compose.web.dom.P {
        org.jetbrains.compose.web.dom.Text("Pass a list and say how to draw one item:")
    }
    io.github.retar.portfolio.components.widgets.CodeBlock("""InfiniteCarousel(
    items = projects,
    modifier = Modifier.padding(top = 24.px),
) { project ->
    ProjectCard(title = project.title, imagePath = project.imagePath)
}
""", lang = "kotlin")
    com.varabyte.kobweb.silk.components.layout.HorizontalDivider()
    org.jetbrains.compose.web.dom.H2(attrs = { id("what-i-d-keep-from-this") }) {
        org.jetbrains.compose.web.dom.Text("What I'd keep from this")
    }
    org.jetbrains.compose.web.dom.P {
        org.jetbrains.compose.web.dom.Text("Animate a transform, not the scroll position. Duplicate the content once and slide it by half. Make the loop seamless by construction rather than by timing. Then pause it on hover, hide the copy from assistive tech, and stop it for people who prefer less motion. That's a whole marquee with no per-frame code at all.")
    }
}
